import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CheckmarkCircle } from '@/components/checkmark-circle';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_overview/pv-installations/',
)({
    component: RouteComponent,
});

type ListProjectObservation = components['schemas']['ListElectricalProjectPVInverterPortalQueryResultInverter'];

type ListPVInstallationChecklistItems = components['schemas']['ViewElectricalProjectPVInstallationPortalQueryResultCheckListItem'];

type ListElectricalProjectObservation = components['schemas']['ListElectricalProjectObservationQueryResultObservation'];

type ListObservationItems = components['schemas']['ListElectricalProjectObservationPortalQueryResultObservation'];

type ListNameValue = {
    Name: string | null;
    Value: string | Date | number | null | boolean;
};

type ListSeverityCode = components['schemas']['ListElectricalProjectObservationQueryResultObservation']['Code'] & {
    Value: string | null;
};

function RouteComponent() {
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();
    const navigate = useNavigate();

    const search = Route.useSearch() as { search?: string };

    const pvInventerQuery = useQuery(portaalQueryOptions.electrical.pvInverter.list({
        ElectricalProjectId: params.projectId ?? null,
    }));

    const observations = useQuery(portaalQueryOptions.electrical.observations.list({
        ElectricalProjectId: params.projectId ?? null,
        ObjectKind: 'PVInverter',
    }));

    const colorCodes = useQuery(portaalQueryOptions.electrical.distributionBoards.list({
        ElectricalProjectId: params.projectId ?? null,
        Kinds: ['ControlBox'],
    }));

    const checklistQuery = useQuery(portaalQueryOptions.electrical.pvInverter.view({
        ElectricalProjectId: params.projectId ?? null,
    }));

    const colors = colorCodes?.data?.Codes?.map(row => ({
        kind: row.Kind,
        hex: row.HexColorCode,
    }));

    const projectQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({ ElectricalProjectId: params.projectId }),
        enabled: !!params.projectId,
    });

    useMemo(() => {
        if (projectQuery.isFetched && projectQuery.isSuccess && projectQuery?.data?.Project?.Templates && projectQuery?.data?.Project?.Templates.length > 0) {
            if (!projectQuery?.data?.Project?.Templates?.some(x => x.InspectionKind === 'Scope12')) {
                return navigate({
                    to: '/customer/locations/$locationId/electrical/projects/$projectId/overview',
                    params: {
                        ...params,
                    },
                });
            }
        }
    }, [navigate, params, projectQuery?.data?.Project?.Templates, projectQuery.isFetched, projectQuery.isSuccess]);

    const columns = useMemo<ColumnDef<ListProjectObservation>[]>(() => ([
        {
            accessorKey: 'ElectricalProjectPVStringId',
            meta: {
                sticky: true,
                width: 120,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListProjectObservation } }) => (
                <DataTable.Cell>
                    <div
                        className="text-sm font-bold text-primary underline min-h-8 flex items-center hover:cursor-pointer"
                        onClick={
                            () => {
                                navigate({
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations/$pvInverterId',
                                    params: {
                                        ...params,
                                        pvInverterId: row.original?.ElectricalProjectPVInverterId ?? '',
                                    },
                                });
                            }
                        }
                    >
                        {row?.original?.Identifier}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'SevereObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.severe')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListProjectObservation } }) => {
                if (!row.original?.SevereObservationCount) {
                    return null;
                }
                return (
                    <div className="flex">
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.severe'),
                                color: colors?.find(c => c.kind === 'Severe')?.hex ?? '#000000',
                                children: row?.original?.SevereObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.severe')}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'SeriousObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.serious')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListProjectObservation } }) => {
                if (!row.original?.SeriousObservationCount) {
                    return null;
                }
                return (
                    <div className="flex">
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.serious'),
                                color: colors?.find(c => c.kind === 'Serious')?.hex ?? '#000000',
                                children: row?.original?.SeriousObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.serious')}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'MinorObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.minor')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListProjectObservation } }) => {
                if (!row.original?.MinorObservationCount) {
                    return null;
                }
                return (
                    <div className="flex items-center min-h-8">
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.minor'),
                                color: colors?.find(c => c.kind === 'Minor')?.hex ?? '#000000',
                                children: row?.original?.MinorObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.minor')}
                        />
                    </div>
                );
            },
        },
        {
            accessorKey: 'MinorObservationCount',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.remarks')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListProjectObservation } }) => {
                if (!row.original?.RemarkObservationCount) {
                    return null;
                }
                return (
                    <div className="flex">
                        <DataTable.Cell
                            variant="observation"
                            props={{
                                label: t('module-electrical.distribution-boards.list.remarks'),
                                color: colors?.find(c => c.kind === 'Remark')?.hex ?? '#000000',
                                children: row?.original?.RemarkObservationCount,
                            }}
                            label={t('module-electrical.distribution-boards.list.remarks')}
                        />
                    </div>
                );
            },
        },

    ]), [colors, navigate, params, t]);

    const tableColumns = useMemo(
        () => pvInventerQuery.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, pvInventerQuery.isLoading],
    );

    const installationsColumn = useMemo(() => [
        {
            accessorKey: 'Description',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListPVInstallationChecklistItems } }) => (
                <DataTable.Cell variant="medium">
                    <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                        {row.original.Description}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Value',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListPVInstallationChecklistItems } }) => {
                let label = row?.original?.NotApplicableLabel;
                if (row?.original?.ConclusionKind && row?.original?.ConclusionKind === 'Approved') {
                    label = row?.original?.PositiveLabel ?? '';
                }
                if (row?.original?.ConclusionKind && row?.original?.ConclusionKind === 'NotApproved') {
                    label = row?.original?.NegativeLabel ?? '';
                }
                if (row?.original?.ConclusionKind && row?.original?.ConclusionKind === 'NotApplicable') {
                    label = row?.original?.NotApplicableLabel ?? '';
                }
                return (
                    <DataTable.Cell variant="medium">
                        <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                            {label}
                        </div>
                    </DataTable.Cell>
                );
            },

        },
    ], [t]);

    const nameValueColumn = useMemo(() => [
        {
            accessorKey: 'Name',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListNameValue } }) => (
                <DataTable.Cell variant="medium">
                    <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                        {row?.original?.Name}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Value',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListNameValue } }) => (
                <DataTable.Cell variant="medium">
                    <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                        {row?.original?.Value ? t('module-electrical.installation.maintenance-inspection-table.columns.yes') : t('module-electrical.installation.maintenance-inspection-table.columns.no')}
                    </div>
                </DataTable.Cell>
            ),
        },
    ], [t]);

    const severity = useMemo<ListSeverityCode[]>(() => [
        {
            Kind: 'Severe',
            Value: t('module-electrical.overview.observations-table.columns.severe'),
        },
        {
            Kind: 'Serious',
            Value: t('module-electrical.overview.observations-table.columns.serious'),
        },
        {
            Kind: 'Minor',
            Value: t('module-electrical.overview.observations-table.columns.minor'),
        },
        {
            Kind: 'Remark',
            Value: t('module-electrical.overview.observations-table.columns.remarks'),
        },
    ], [t]);

    const notificationColumns = useMemo(() => [
        {
            accessorKey: 'Category',
            meta: {
                sticky: true,
                width: 10,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.observations-table.columns.groupKind')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <DataTable.Cell variant="medium">
                    <div
                        onClick={
                            () => {
                                navigate({
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations/$pvInverterId/observations/$observationId',
                                    params: {
                                        ...params,
                                        pvInverterId: row.original?.ElectricalProjectPVInverterId ?? '',
                                        observationId: row?.original?.ElectricalProjectObservationId ?? '',
                                    },
                                });
                            }
                        }
                        className="text-sm font-bold text-primary underline hover:cursor-pointer flex items-center min-h-8 "
                    >
                        {severity.find(s => s.Kind === row?.original?.CodeKind)?.Value || t('module-electrical.report.code-kinds.undefined')}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'HexColorCode',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.pv-installations.table.severity')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListElectricalProjectObservation } }) => (
                <div className="flex items-center min-h-8">
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: t('module-electrical.pv-installations.table.severity'),
                            color: colors?.find(c => c.kind === row?.original?.CodeKind)?.hex ?? '',
                            children: null,
                        }}
                        label={severity.find(s => s.Kind === row?.original?.CodeKind)?.Value ?? ''}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'Status',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.status')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <div className="flex items-center min-h-8">
                    <CheckmarkCircle checked={!!row?.original?.IsFixed} />
                </div>
            ),
        },
        {
            accessorKey: 'LocationRemark',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.installation.location-description.title')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="medium">
                    <div className="flex items-center min-h-8">
                        {row?.original?.LocationRemark}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Remark',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.installation.notifications-table.header.comment')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Remark && row.original.Remark.length > 40
                        ? `${row.original.Remark.slice(0, 40)}...`
                        : row.original.Remark}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Date',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.date')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: any } }) => (
                <DataTable.Cell variant="medium">
                    <div className="flex items-center min-h-8">
                        {row?.original?.Date ? row.original.Date.split('T')[0] : ''}
                    </div>
                </DataTable.Cell>
            ),
        },
    ], [colors, navigate, params, severity, t]);

    const tableData = useMemo<ListProjectObservation[]>(
        () => {
            if (pvInventerQuery.isError) {
                toast.error(pvInventerQuery.error?.Message);
                return [];
            }

            const searchTerm = search.search?.toLowerCase() || '';
            return pvInventerQuery.data?.Inverters?.filter((inventer) => {
                if (!searchTerm)
                    return true;

                return [
                    inventer.Identifier || '',
                    inventer.NoneObservationCount || '',
                    inventer.MinorObservationCount || '',
                    inventer.RemarkObservationCount || '',
                    inventer.SevereObservationCount || '',
                    inventer.SeriousObservationCount || '',
                ].some(field => field?.toString().toLowerCase().includes(searchTerm));
            }) || [];
        },
        [pvInventerQuery.isError, pvInventerQuery.data?.Inverters, pvInventerQuery.error?.Message, search.search],
    );

    if (pvInventerQuery.isLoading || colorCodes.isLoading || checklistQuery.isLoading || projectQuery.isLoading) {
        return <LoadingPage />;
    }
    return (
        // table
        <div className="flex flex-col gap-4 lg:gap-9">
            <div className="w-full">
                <DataTable.TableHead
                    title={t('module-electrical.pv-installations.title')}
                    label={`${pvInventerQuery?.data?.Inverters?.length ?? 0} ${t(`module-electrical.pv-installations.titleFormal`)}`}
                    subtitle={t('module-electrical.pv-installations.table.subtitle')}
                    search={Route}
                />
                <DataTable
                    className="rounded-tl-none rounded-tr-none"
                    columns={tableColumns}
                    data={tableData}
                    pageSize={10}
                />

            </div>

            <div className="w-full h-px block bg-border-primary"></div>

            {/* algemene meldingne */}
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.generalObservations.title')}
                    </div>
                    <div className="max-w-xs ">
                        {t('module-electrical.pv-installations.generalObservations.subtitle')}
                    </div>
                </div>
                <div className="col-span-2">
                    {observations?.data?.Observations && observations?.data?.Observations.length > 0
                        ? (
                                <DataTable
                                    columns={notificationColumns}
                                    data={observations?.data?.Observations ?? []}
                                    pageSize={5}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noObservationsFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>

            {/* TODO: algemene indruk van de installatie (fotos van achterliggend PV instlatie) */}

            {/* <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 font-semibold">
                    {t('module-electrical.pv-installations.generalImpression')}
                </div>
                <div className="col-span-2">

                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div> */}

            {/* documentatie en installatie */}
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 font-semibold">
                    {t('module-electrical.pv-installations.documentsInstallation.title')}
                </div>
                <div className="col-span-2">
                    {checklistQuery?.data?.CheckListItems && checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12InspectionPlanDocument').length > 0
                        ? (
                                <DataTable
                                    columns={installationsColumn}
                                    data={checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12InspectionPlanDocument') ?? []}
                                    pageSize={10}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noDocumentInstallationFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>
            {/* algemene opmerkingen */}
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 font-semibold">
                    {t('module-electrical.pv-installations.generalRemarks.title')}
                </div>
                <div className="col-span-2">
                    {checklistQuery?.data?.CheckListItems && checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12GeneralRemarks').length > 0
                        ? (
                                <DataTable
                                    columns={installationsColumn}
                                    data={checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12GeneralRemarks') ?? []}
                                    pageSize={10}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noGeneralRemarksFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>
            {/* verheffing */}
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 flex flex-col">
                    <div className="font-semibold">
                        {t('module-electrical.pv-installations.elevation.title')}
                    </div>
                    <div className="max-w-xs ">
                        {t('module-electrical.pv-installations.elevation.subtitle')}
                    </div>
                </div>
                <div className="col-span-2">
                    <DataTable
                        columns={nameValueColumn}
                        data={[{
                            Name: t('module-electrical.pv-installations.elevation.table.name'),
                            Value: checklistQuery?.data?.Inspection?.IsPVInstallationEquipotentialBondingPassed ?? false,
                        }]}
                        pageSize={10}
                    />
                </div>
            </div>
            {/* visuele controle */}
            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 flex flex-col">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.visualControl.title')}
                    </div>
                    <div className="max-w-xs ">
                        {t('module-electrical.pv-installations.visualControl.subtitle')}
                    </div>
                </div>
                <div className="col-span-2">
                    <DataTable
                        columns={installationsColumn}
                        data={[
                            {
                                Description: t('module-electrical.pv-installations.visualControl.table.name'),
                                Kind: 'Scope12VisualInspection',
                                ConclusionKind: checklistQuery?.data?.Inspection?.IsPVInstallationConstructionCompanyKnown ? 'Approved' : 'NotApproved',
                                PositiveLabel: t('module-electrical.installation.maintenance-inspection-table.columns.yes'),
                                NegativeLabel: t('module-electrical.installation.maintenance-inspection-table.columns.no'),
                            },
                            ...(checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12VisualInspection')
                                ?.map(row => ({
                                    ...row,
                                })) ?? []),
                        ]}
                        pageSize={100}
                    />
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>

            {/* Aanvullende contorlepunten */}
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 font-semibold">
                    {t('module-electrical.pv-installations.additionalControlePoints')}
                </div>
                <div className="col-span-2">
                    {checklistQuery?.data?.CheckListItems && checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12AdditionalInspectionPoints').length > 0
                        ? (
                                <DataTable
                                    columns={installationsColumn}
                                    data={checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12AdditionalInspectionPoints') ?? []}
                                    pageSize={10}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noAdditionalControlPoints')}</p>
                                </div>
                            )}
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>

            {/* Documentatie DC-zijnde */}

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 font-semibold">
                    {t('module-electrical.pv-installations.documentDcSide')}
                </div>
                <div className="col-span-2">
                    {checklistQuery?.data?.CheckListItems && checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12DocumentationDCSide').length > 0
                        ? (
                                <DataTable
                                    columns={installationsColumn}
                                    data={checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12DocumentationDCSide') ?? []}
                                    pageSize={10}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noDCSideFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>

            {/* Handleidingen/dataspecificaties */}
            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0 font-semibold">
                    {t('module-electrical.pv-installations.manualDataSpecifications')}
                </div>
                <div className="col-span-2">
                    {checklistQuery?.data?.CheckListItems && checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12ManualAndDataSpecifications').length > 0
                        ? (
                                <DataTable
                                    columns={installationsColumn}
                                    data={checklistQuery?.data?.CheckListItems?.filter(row => row.Kind === 'Scope12ManualAndDataSpecifications') ?? []}
                                    pageSize={10}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noManualAndDataSpecificationsFound')}</p>
                                </div>
                            )}
                </div>
            </div>
        </div>
    );
}
